import { Component, ElementRef, inject, OnInit, signal, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ModalService } from '@card-software/library';
import { LocaleHelperService } from '@app/services/locale-helper.service';
import { getMonthOptions } from '@app/utilities/date.util';
import { MachineAssignment, MachineAssignmentImportCommand } from '../../../models/commands/machine-assignment-import-command';
import {
  MACHINE_ASSIGNMENT_AREA_COST_CENTERS,
  MachineAssignmentArea,
  MachineAssignmentAreaOption,
} from '../../../models/production/machine-assignment-area.enum';
import { MachineAssignmentShift, MachineAssignmentShiftOption } from '../../../models/production/machine-assignment-shift.enum';
import { ProductionGroup } from '../../../models/production/production-group.model';
import { MachineAssignmentProxyService } from '../../../proxies/production/machine-assignment.proxy.service';
import { ProductionGroupProxyService } from '../../../proxies/production/production-group.proxy.service';
import { MachineAssignmentPreviewModalComponent } from './_modals/machine-assignment-preview-modal/machine-assignment-preview-modal.component';

@Component({
  selector: 'app-machine-assignment',
  templateUrl: './machine-assignment.component.html',
  styleUrl: './machine-assignment.component.scss',
})
export class MachineAssignmentComponent implements OnInit {
  private readonly machineAssignmentProxyService = inject(MachineAssignmentProxyService);
  private readonly messageService = inject(MessageService);
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly productionGroupProxyService = inject(ProductionGroupProxyService);
  private readonly localeHelperService = inject(LocaleHelperService);

  @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;

  ngOnInit(): void {
    this.loadRecords();
    this.loadGroups();
  }

  isLoading = signal(0);
  lastRecords = signal<MachineAssignment[]>([]);
  groups = signal<ProductionGroup[]>([]);
  filterMode = signal<'dateShift' | 'costCenterMonth'>('dateShift');
  sampleTemplateUrl = 'assets/templates/machine-assignment-sample.xlsx';

  filterDate = new FormControl<Date | null>(null);
  filterShift = new FormControl<number | null>(null);
  filterCostCenter = new FormControl<string | null>(null);
  filterYear = new FormControl<number | null>(null);
  filterMonth = new FormControl<number | null>(null);

  readonly yearOptions: { label: string; value: number }[] = (() => {
    const current = new Date().getFullYear();
    return [current - 2, current - 1, current, current + 1].map((y) => ({ label: String(y), value: y }));
  })();

  area = new FormControl<MachineAssignmentArea | null>(null, [Validators.required]);
  date = new FormControl<Date | null>(null, [Validators.required]);
  shift = new FormControl<MachineAssignmentShift | null>(null, [Validators.required]);
  file = new FormControl<File | null>(null, [Validators.required]);

  form = new FormGroup({
    area: this.area,
    date: this.date,
    shift: this.shift,
    file: this.file,
  });

  get areaOptions(): MachineAssignmentAreaOption[] {
    return [
      {
        label: this.translateService.instant('MachineAssignment.area_Knitting') || 'Knitting',
        value: MachineAssignmentArea.Knitting,
        costCenterCode: MACHINE_ASSIGNMENT_AREA_COST_CENTERS[MachineAssignmentArea.Knitting],
      },
      {
        label: this.translateService.instant('MachineAssignment.area_RollQc') || 'Roll QC',
        value: MachineAssignmentArea.RollQc,
        costCenterCode: MACHINE_ASSIGNMENT_AREA_COST_CENTERS[MachineAssignmentArea.RollQc],
      },
      {
        label: this.translateService.instant('MachineAssignment.area_Batching') || 'Batching',
        value: MachineAssignmentArea.Batching,
        costCenterCode: MACHINE_ASSIGNMENT_AREA_COST_CENTERS[MachineAssignmentArea.Batching],
      },
      {
        label: this.translateService.instant('MachineAssignment.area_Dyeing') || 'Dyeing',
        value: MachineAssignmentArea.Dyeing,
        costCenterCode: MACHINE_ASSIGNMENT_AREA_COST_CENTERS[MachineAssignmentArea.Dyeing],
      },
      {
        label: this.translateService.instant('MachineAssignment.area_Finishing') || 'Finishing',
        value: MachineAssignmentArea.Finishing,
        costCenterCode: MACHINE_ASSIGNMENT_AREA_COST_CENTERS[MachineAssignmentArea.Finishing],
      },
      {
        label: this.translateService.instant('MachineAssignment.area_Rolling') || 'Rolling',
        value: MachineAssignmentArea.Rolling,
        costCenterCode: MACHINE_ASSIGNMENT_AREA_COST_CENTERS[MachineAssignmentArea.Rolling],
      },
    ];
  }

  get shiftOptions(): MachineAssignmentShiftOption[] {
    return [
      {
        label: this.translateService.instant('MachineAssignment.shift_Matutino') || 'Matutino (Morning)',
        value: MachineAssignmentShift.Matutino,
      },
      {
        label: this.translateService.instant('MachineAssignment.shift_Nocturno') || 'Nocturno (Night)',
        value: MachineAssignmentShift.Nocturno,
      },
    ];
  }

  get canUploadFile(): boolean {
    return this.area.valid && this.date.valid && this.shift.valid;
  }

  get selectedCostCenterCode(): string {
    const selected = this.areaOptions.find((o) => o.value === this.area.value);
    return selected?.costCenterCode ?? '';
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const selected = input.files && input.files.length ? input.files[0] : null;

    if (!selected) {
      this.file.setValue(null);
      return;
    }

    const isXlsx = selected.name.toLowerCase().endsWith('.xlsx');
    if (!isXlsx) {
      this.file.setValue(null);
      if (this.fileInputRef?.nativeElement) {
        this.fileInputRef.nativeElement.value = '';
      }
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('MachineAssignment.invalid_file') || 'Invalid file',
        detail: this.translateService.instant('MachineAssignment.invalid_file_detail') || 'Only .xlsx files are allowed.',
        life: 3000,
      });
      return;
    }

    this.file.setValue(selected);
    this.file.markAsTouched();
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const shiftValue = this.shift.value;
    const fileValue = this.file.value;

    if (shiftValue === null || !fileValue) {
      this.form.markAllAsTouched();
      return;
    }

    const previewCommand: MachineAssignmentImportCommand = {
      shift: shiftValue,
      file: fileValue,
      costCenterCode: this.selectedCostCenterCode,
      preview: true,
      override: false,
    };

    this.startLoading();
    this.machineAssignmentProxyService
      .import(previewCommand)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response?.success && response.data) {
            this.modalService
              .open(MachineAssignmentPreviewModalComponent, { modalClass: 'modal_large' }, { previewResult: response.data })
              .subscribe((modalResult) => {
                if (modalResult.action === 'save') {
                  this.executeSave(shiftValue, fileValue, modalResult.data?.override ?? false);
                } else {
                  this.loadRecords();
                }
              });
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.translateService.instant('MachineAssignment.import_error') || 'Could not import the file. Please try again.',
              life: 3000,
            });
          }
        },
        error: (error) => {
          console.error('Error previewing machine assignment file:', error);
        },
      });
  }

  private executeSave(shift: MachineAssignmentShift, file: File, override: boolean): void {
    const saveCommand: MachineAssignmentImportCommand = {
      shift,
      file,
      costCenterCode: this.selectedCostCenterCode,
      preview: false,
      override,
    };

    this.startLoading();
    this.machineAssignmentProxyService
      .import(saveCommand)
      .pipe(finalize(() => this.stopLoading()))
      .subscribe({
        next: (response) => {
          if (response?.success) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: this.translateService.instant('MachineAssignment.import_success') || 'File imported successfully.',
              life: 3000,
            });
            this.resetForm();
            this.loadRecords();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: this.translateService.instant('MachineAssignment.import_error') || 'Could not import the file. Please try again.',
              life: 3000,
            });
          }
        },
        error: (error) => {
          console.error('Error saving machine assignment file:', error);
        },
      });
  }

  get monthOptions(): { label: string; value: number }[] {
    return getMonthOptions(this.localeHelperService);
  }

  get canApplyDateShiftFilter(): boolean {
    return this.filterDate.value !== null && this.filterShift.value !== null;
  }

  get canApplyCostCenterFilter(): boolean {
    return !!this.filterCostCenter.value && this.filterYear.value !== null && this.filterMonth.value !== null;
  }

  onApplyFilter(): void {
    if (this.filterMode() === 'dateShift') {
      if (!this.filterDate.value || this.filterShift.value === null) return;
      const d = this.filterDate.value;
      const dateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      this.startLoading();
      this.machineAssignmentProxyService
        .getByDateShift(dateStr, this.filterShift.value)
        .pipe(finalize(() => this.stopLoading()))
        .subscribe({
          next: (response) => {
            if (response?.success) this.lastRecords.set(response.data ?? []);
          },
          error: (error) => {
            console.error('Error filtering by date/shift:', error);
          },
        });
    } else {
      if (!this.filterCostCenter.value || this.filterYear.value === null || this.filterMonth.value === null) return;
      this.startLoading();
      this.machineAssignmentProxyService
        .getByCostCenterAndMonth(this.filterCostCenter.value, this.filterYear.value, this.filterMonth.value)
        .pipe(finalize(() => this.stopLoading()))
        .subscribe({
          next: (response) => {
            if (response?.success) this.lastRecords.set(response.data ?? []);
          },
          error: (error) => {
            console.error('Error filtering by cost center/month:', error);
          },
        });
    }
  }

  onResetFilter(): void {
    this.filterDate.reset();
    this.filterShift.reset();
    this.filterCostCenter.reset();
    this.filterYear.reset();
    this.filterMonth.reset();
    this.loadRecords();
  }

  getGroupName(ref: string | null): string {
    if (!ref) return '-';
    const lang = (this.translateService.currentLang ?? 'en') as 'en' | 'es';
    const group = this.groups().find((g) => g.id === ref);
    if (!group) return ref;
    return group.name[lang] || group.name['en'] || ref;
  }

  private loadGroups(): void {
    this.productionGroupProxyService.getAll().subscribe({
      next: (response) => {
        if (response?.success && response.data) {
          this.groups.set(response.data);
        }
      },
      error: (error) => console.error('Error loading production groups:', error),
    });
  }

  private loadRecords(): void {
    this.machineAssignmentProxyService.getAll().subscribe({
      next: (response) => {
        if (response?.success && response.data) {
          this.lastRecords.set(response.data);
        }
      },
      error: (error) => console.error('Error loading machine assignments:', error),
    });
  }

  private resetForm(): void {
    this.form.reset();
    if (this.fileInputRef?.nativeElement) {
      this.fileInputRef.nativeElement.value = '';
    }
  }

  private startLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private stopLoading(): void {
    this.isLoading.update((v) => v - 1);
  }

  public downloadTemplate(): void {
    const link =
      'https://sierra-erp-assets.s3.us-east-1.amazonaws.com/docs/Formato%20de%20Asignacio%CC%81n%20de%20Ma%CC%81quinas%20NT%20Sierra.xlsx';
    window.open(link, '_blank');
  }
}
